import { useEffect } from 'react';
import { isThemePreview } from '../api/previewVisit';
import { isThemeEditor } from '../editor/editorVisit';
import { cardReturnOf, heldCardOrder } from './cardPayment';
import { useCommerce } from './ShopCommerce';

// The thank-you page is reached only by placing an order, as Shopify's order status page is: a visit that typed
// or bookmarked /thank-you with no order of its own is sent to the home page instead of an empty receipt. A
// return from Stripe is left to settle its payment, and the builder and theme previews draw their sample.

// Reports whether this visit is coming back from Stripe for an order it handed over.
const backFromCard = (): boolean => {
  const back = cardReturnOf(window.location.search);
  return Boolean(heldCardOrder() && (back.sessionId || back.cancelled));
};

// Replaces /thank-you with the home page, so Back never lands on the empty page again.
const leaveForHome = (): void => {
  window.history.replaceState({}, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
};

/** How long the page waits for the order a checkout just placed: the address can move a render before the order lands. */
const ORDER_GRACE_MS = 400;

// Sends an order-less visit to /thank-you home once the order a checkout placed has had time to arrive; draws nothing.
export function ThankYouGuard({ page }: { page: string }): null {
  const order = useCommerce()?.order ?? null;
  useEffect(() => {
    if (page !== 'thankyou' || order || isThemeEditor() || isThemePreview() || backFromCard()) return undefined;
    // The order arriving re-runs this effect, whose cleanup cancels the move.
    const timer = window.setTimeout(leaveForHome, ORDER_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [page, order]);
  return null;
}
